import { useState, type CSSProperties } from 'react';

import { ContactSchema, type Contact } from '@kilat/shared';
import { Button, inputStyle, Surface } from '@kilat/ui';

import { brandingRepoFor } from '../lib/repositories/branding-repo.js';
import { logger } from '../lib/logger.js';
import { useI18n } from '../lib/use-i18n.js';

/**
 * The web links an operator's customers see — Google review, Instagram,
 * Facebook, TikTok, website (Share & grow).
 *
 * `BrandingSchema.contact` has carried the four social fields since the
 * white-label app shipped, and the customer app's About screen renders them,
 * but nothing in the dashboard could set them. The Google review link is the
 * one that matters most now: the customer app offers it after every rating.
 *
 * Saved as one replacement of the `contact` map, built from the fields the
 * operator did NOT touch plus the ones they did — so a cleared field is really
 * removed rather than written as an empty string the schema would reject.
 */

const FIELDS = ['googleReview', 'instagram', 'facebook', 'tiktok', 'website'] as const;
type LinkField = (typeof FIELDS)[number];

type Status = 'idle' | 'saving' | 'saved' | 'error';

function isWebUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function ContactLinksCard({
  tenantId,
  contact,
  onSaved,
}: {
  readonly tenantId: string;
  readonly contact: Contact;
  readonly onSaved: (next: Contact) => void;
}) {
  const { t } = useI18n();
  const [values, setValues] = useState<Record<LinkField, string>>(() => ({
    googleReview: contact.googleReview ?? '',
    instagram: contact.instagram ?? '',
    facebook: contact.facebook ?? '',
    tiktok: contact.tiktok ?? '',
    website: contact.website ?? '',
  }));
  const [status, setStatus] = useState<Status>('idle');

  const invalid = FIELDS.filter((f) => values[f].trim() !== '' && !isWebUrl(values[f].trim()));

  async function save(): Promise<void> {
    if (invalid.length > 0) return;
    setStatus('saving');
    // Everything the operator did not edit here (phone, WhatsApp, address…),
    // minus any undefined — Firestore rejects an undefined field value.
    const rest: Record<string, string> = {};
    for (const [k, v] of Object.entries(contact)) {
      if (typeof v === 'string' && !(FIELDS as readonly string[]).includes(k)) rest[k] = v;
    }
    const links: Partial<Record<LinkField, string>> = {};
    for (const f of FIELDS) {
      const v = values[f].trim();
      if (v) links[f] = v;
    }
    const parsed = ContactSchema.safeParse({ ...rest, ...links });
    if (!parsed.success) {
      setStatus('error');
      return;
    }
    try {
      await brandingRepoFor(tenantId).update('current', { contact: { ...rest, ...links } });
      onSaved(parsed.data);
      setStatus('saved');
    } catch (e) {
      logger.error('contact links save failed', { error: String(e) });
      setStatus('error');
    }
  }

  return (
    <Surface as="section" id="links" aria-labelledby="contact-links-title">
      <h2 id="contact-links-title" style={titleStyle}>
        {t('dashboard.contactLinks.title')}
      </h2>
      <p style={mutedStyle}>{t('dashboard.contactLinks.subtitle')}</p>

      <div style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
        {FIELDS.map((f) => {
          const bad = invalid.includes(f);
          return (
            <label key={f} style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--text-meta)' }}>
                {t(`dashboard.contactLinks.${f}`)}
              </span>
              <input
                type="url"
                inputMode="url"
                autoComplete="url"
                placeholder="https://"
                value={values[f]}
                aria-invalid={bad}
                onChange={(e) => {
                  setValues((v) => ({ ...v, [f]: e.target.value }));
                  if (status !== 'saving') setStatus('idle');
                }}
                style={{
                  ...inputStyle,
                  ...(bad ? { borderColor: 'var(--color-danger)' } : null),
                }}
              />
              {f === 'googleReview' ? (
                <>
                  <span style={mutedStyle}>{t('dashboard.contactLinks.googleReviewHint')}</span>
                  <span style={mutedStyle}>{t('dashboard.contactLinks.googlePolicy')}</span>
                </>
              ) : null}
              {bad ? (
                <span role="alert" style={{ ...mutedStyle, color: 'var(--color-danger)' }}>
                  {t('dashboard.contactLinks.invalid')}
                </span>
              ) : null}
            </label>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="primary"
          disabled={status === 'saving' || invalid.length > 0}
          onClick={() => void save()}
        >
          {status === 'saving'
            ? t('dashboard.contactLinks.saving')
            : t('dashboard.contactLinks.save')}
        </Button>
        {status === 'saved' ? (
          <span role="status" style={mutedStyle}>
            {t('dashboard.contactLinks.saved')}
          </span>
        ) : null}
        {status === 'error' ? (
          <span role="alert" style={{ ...mutedStyle, color: 'var(--color-danger)' }}>
            {t('dashboard.contactLinks.error')}
          </span>
        ) : null}
      </div>
    </Surface>
  );
}

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 650,
};

const mutedStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};
